/**
 * Drag-and-drop for the chat surface: drop files anywhere on the conversation
 * and they are staged exactly like a picked file. `isDragging` drives the
 * "Drop to attach" overlay. A depth counter is used because dragenter/dragleave
 * fire for every child element the pointer crosses.
 */
import { useCallback, useRef, useState } from 'react';
import type { DragEvent } from 'react';
import { dragHasFiles, filesFromTransfer } from '../composer-input';

export function useFileDrop(onFiles: (files: File[]) => void) {
  const [isDragging, setIsDragging] = useState(false);
  const depth = useRef(0);

  const onDragEnter = useCallback((e: DragEvent) => {
    if (!dragHasFiles(e.dataTransfer)) return;
    e.preventDefault();
    depth.current += 1;
    setIsDragging(true);
  }, []);

  const onDragOver = useCallback((e: DragEvent) => {
    // Without preventDefault the browser would navigate to the dropped file.
    if (dragHasFiles(e.dataTransfer)) e.preventDefault();
  }, []);

  const onDragLeave = useCallback((e: DragEvent) => {
    if (!dragHasFiles(e.dataTransfer)) return;
    depth.current = Math.max(0, depth.current - 1);
    if (depth.current === 0) setIsDragging(false);
  }, []);

  const onDrop = useCallback(
    (e: DragEvent) => {
      if (!dragHasFiles(e.dataTransfer)) return;
      e.preventDefault();
      depth.current = 0;
      setIsDragging(false);
      const files = filesFromTransfer(e.dataTransfer);
      if (files.length > 0) onFiles(files);
    },
    [onFiles],
  );

  return {
    isDragging,
    dropProps: { onDragEnter, onDragOver, onDragLeave, onDrop },
  };
}
